<!-- eslint-disable vue/html-self-closing -->
<template>
  <div>
    <div v-if="data.length > 0">
      <div
        v-if="!hasPopupOnly"
        class="carousel-wrapper"
      >
        <b-carousel
          v-model="activeIndex"
          :indicator="false"
          :arrow="data.length > 1"
          :items-to-show="slidesPerView"
          :icon-size="iconSize"
          :has-drag="true"
          :arrow-hover="!isSmallScreen"
          :autoplay="autoPlay"
          class="is325"
        >
          <b-carousel-item
            v-for="(image, index) in data"
            :key="index"
          >
            <div class="carousel-content">
              <figure class="image w-full">
                <!-- Show the lightweight thumbnail; the full-resolution
                     original is used by the popup gallery / download. Only the
                     active slide (and neighbours) load, so cards with many
                     photos don't download them all at once. -->
                <img
                  v-if="loaded.includes(index) && !isVideoItem(image)"
                  :src="image.thumb_url || image.url"
                  loading="lazy"
                  class="rounded-top is325 is-image-cover"
                  :class="{ 'is-clickable': hasPopupGallery || clickableImages }"
                  :alt="image.file_name"
                  @click="onImageClick(index)"
                />
                <!-- Videos: show the extracted poster with a play badge -->
                <div
                  v-else-if="loaded.includes(index) && isVideoItem(image)"
                  class="video-poster-wrap"
                  :class="{ 'is-clickable': hasPopupGallery || clickableImages }"
                  @click="onImageClick(index)"
                >
                  <img
                    v-if="image.thumb_url && image.thumb_url !== image.url"
                    :src="image.thumb_url"
                    loading="lazy"
                    class="rounded-top is325 is-image-cover"
                    :alt="image.file_name"
                  />
                  <div
                    v-else
                    class="rounded-top is325 is-image-cover image-placeholder"
                  />
                  <span class="video-play-badge">
                    <i class="mdi mdi-play-circle mdi-48px" />
                  </span>
                </div>
                <div
                  v-else
                  class="rounded-top is325 is-image-cover image-placeholder"
                />
              </figure>
              <a
                href="image.url"
                class="button is-primary download-btn"
                download
              >
                {{ $t('download').capitalize() }}
              </a>
            </div>
          </b-carousel-item>
        </b-carousel>

        <!-- Windowed pagination dots: render at most `maxDots`, kept centred on
             the active slide (like a paginator showing 10 of 100 pages). -->
        <div
          v-if="data.length > 1"
          class="slider-dots"
        >
          <button
            v-for="i in windowIndices"
            :key="i"
            type="button"
            class="slider-dot"
            :class="{ 'is-active': i === activeIndex }"
            :aria-label="`${i + 1} / ${data.length}`"
            @click="activeIndex = i"
          />
        </div>
      </div>
      <div v-if="hasPopupOnly">
        <button
          class="button is-text is-small"
          type="button"
          @click="setPopupGalleryItem(0)"
        >
          {{ label }}
        </button>
      </div>
    </div>
    <section
      v-else
      class="hero is-medium is-gray"
    >
      <div
        class="is325 rounded-top has-text-centered is-flex is-background-gray border-b border-gary is-align-items-center is-justify-content-center"
      >
        <p class="is-family-display">
          {{ $t("not_found_image").capitalize() }}
        </p>
      </div>
    </section>

    <vue-easy-lightbox
      v-if="hasPopupGallery && data.length > 0"
      :visible="isShowPopupGallery"
      :imgs="mediaForLightbox"
      :index="activePopupGalleryItem"
      @hide="closePopupGallery"
    ></vue-easy-lightbox>
  </div>
</template>

<script>
import { i18n } from "@/services/i18n";
import VueEasyLightbox from "vue-easy-lightbox";

export default {
  components: {
    VueEasyLightbox,
  },
  props: {
    slidesPerView: {
      default: 1,
      type: Number,
    },
    gap: {
      default: 50,
      type: Number,
    },
    arrowBoth: {
      default: true,
      type: Boolean,
    },
    arrowHover: {
      default: true,
      type: Boolean,
    },
    autoPlay: {
      default: false,
      type: Boolean,
    },
    iconSize: {
      default: "",
      type: String,
    },
    data: {
      default() {
        return [];
      },
      type: Array,
    },
    hasPopupGallery: {
      default: false,
      type: Boolean,
    },
    // Emit "image-click" (with the slide index) when an image is clicked,
    // and show the pointer cursor — used by the public listing to open the
    // property page.
    clickableImages: {
      default: false,
      type: Boolean,
    },
    hasPopupOnly: {
      default: false,
      type: Boolean,
    },
    label: {
      default: i18n.t("view").capitalize(),
      type: String,
    },
  },
  data() {
    return {
      // Touch screens have no hover: keep the arrows always visible there
      isSmallScreen: window.matchMedia("(max-width: 768px)").matches,
      activePopupGalleryItem: 0,
      isShowPopupGallery: false,
      activeIndex: 0,
      maxDots: 10,
      // Slide indexes whose image has been loaded (first one eagerly).
      loaded: [0],
    };
  },
  watch: {
    // Load slides on demand as the user navigates the carousel.
    activeIndex(index) {
      [index - 1, index, index + 1].forEach((i) => {
        if (i >= 0 && i < this.data.length && !this.loaded.includes(i)) {
          this.loaded.push(i);
        }
      });
    },
  },
  computed: {
    mediaForLightbox() {
      var images = [];
      this.data.forEach((element) => {
        images.push(element.url);
      });
      return images;
    },
    // Indices of the dots to display: all of them when there are few images,
    // otherwise a sliding window of `maxDots` centred on the active slide.
    windowIndices() {
      const total = this.data.length;
      const max = this.maxDots;
      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i);
      }
      let start = this.activeIndex - Math.floor(max / 2);
      if (start < 0) start = 0;
      if (start > total - max) start = total - max;
      return Array.from({ length: max }, (_, i) => start + i);
    },
  },
  methods: {
    isVideoItem(m) {
      return !!(m && m.mime_type && m.mime_type.startsWith("video/"));
    },
    onImageClick(i) {
      if (this.clickableImages) {
        this.$emit("image-click", i);
      }
      if (this.hasPopupGallery) {
        this.setPopupGalleryItem(i);
      }
    },
    setPopupGalleryItem(i) {
      this.activePopupGalleryItem = i;
      this.isShowPopupGallery = true;
    },
    closePopupGallery(event) {
      this.isShowPopupGallery = false;
      this.activePopupGalleryItem = 0;
    },
  },
};
</script>

<style scoped>
.video-poster-wrap {
  position: relative;
}
.video-play-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

.is325 {
  width: 100%;
  height: 247px;
}
.border-b {
  border-bottom: solid 1px;
}
.border-gary {
  border-color: #f4f4f4;
}
.image_center {
  height: auto;
  width: 100%;
  object-fit: cover;
}
.image-placeholder {
  background: #f4f4f4;
}
.rounded {
  border-radius: 9px;
}
.rounded-top {
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
}
.is-background-gray {
  background: #f9f9f9;
}

@media only screen and (max-width: 768px) {
  .is325 {
    width: 100%;
    height: 175px;
  }
}
.carousel-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.download-btn {
  margin-top: 10px;
  z-index: 10;
}

/* Windowed pagination dots overlaid on the bottom of the image */
.carousel-wrapper {
  position: relative;
}
.slider-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  pointer-events: none;
}
.slider-dot {
  pointer-events: auto;
  width: 9px;
  height: 9px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.15s ease;
}
.slider-dot.is-active {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.4);
  transform: scale(1.25);
}

</style>
<style>
.carousel .carousel-items {
  height: 247px !important;
}
/* Match the reduced image height on mobile so overlays sit on the image. */
@media only screen and (max-width: 768px) {
  .carousel .carousel-items {
    height: 175px !important;
  }
}
.is-image-cover {
  object-fit: cover !important;
}
.w-full {
  width: 100%;
}
</style>
